import type { RidershipFilterCatalogDirection, RidershipFilterCatalogRoute, RidershipHistoryRecord } from '../../../types/ridershipTypes';
import type { DirectionCatalogOption } from '../average-weekday-by-stop/averageWeekdayByStopDirectionUtils';

function normalizeRouteLookupKey(value: string | null | undefined): string {
    return String(value ?? '').trim().toLowerCase();
}

export function buildDirectionsLookupByRouteKey(
    routes: RidershipFilterCatalogRoute[] | null | undefined,
): Map<string, RidershipFilterCatalogDirection[]> {
    const byRouteKey = new Map<string, RidershipFilterCatalogDirection[]>();

    for (const route of routes ?? []) {
        const directions = route.directions ?? [];
        const routeIdKey = normalizeRouteLookupKey(route.routeId);
        if (routeIdKey) {
            byRouteKey.set(routeIdKey, directions);
        }

        const routeNameKey = normalizeRouteLookupKey(route.routeName);
        if (routeNameKey) {
            byRouteKey.set(routeNameKey, directions);
        }
    }

    return byRouteKey;
}

export function getCatalogDirectionsForRouteKey(
    lookup: Map<string, RidershipFilterCatalogDirection[]>,
    routeKey: string | null | undefined,
): RidershipFilterCatalogDirection[] {
    const normalizedKey = normalizeRouteLookupKey(routeKey);
    if (!normalizedKey) {
        return [];
    }

    return lookup.get(normalizedKey) ?? [];
}

function normalizeInternalId(value: string | null | undefined): string {
    return String(value ?? '').trim().toLowerCase();
}

function sumNumericField(left: number | null | undefined, right: number | null | undefined): number {
    const leftValue = Number.isFinite(left as number) ? Number(left) : 0;
    const rightValue = Number.isFinite(right as number) ? Number(right) : 0;
    return leftValue + rightValue;
}

function sumOptionalField(
    left: number | null | undefined,
    right: number | null | undefined,
): number | undefined {
    if (left == null && right == null) {
        return undefined;
    }

    return sumNumericField(left, right);
}

export function buildDirectionOptionsFromCatalog(
    directions: RidershipFilterCatalogDirection[] | null | undefined,
): DirectionCatalogOption[] {
    return (directions ?? [])
        .map((direction): DirectionCatalogOption => ({
            id: direction.directionVariantId,
            displayName: direction.firstStopName
                ? `${direction.directionLabel} (${direction.firstStopName})`
                : direction.directionLabel,
            internalId: direction.directionVariantInternalIds?.[0] || '',
            internalIds: direction.directionVariantInternalIds || [],
            trips: (direction.trips ?? []).map(trip => ({
                tripId: trip.tripId,
                shortTripId: trip.shortTripId,
                friendlyName: trip.friendlyName,
                tripHeadsign: trip.tripHeadsign,
            })),
        }))
        .filter(option => option.id.trim().length > 0);
}

export function filterRowsByDirectionInternalIds(
    rows: RidershipHistoryRecord[] | null | undefined,
    internalIds: string[] | null | undefined,
): RidershipHistoryRecord[] {
    const list = rows ?? [];
    const normalizedInternalIds = (internalIds ?? [])
        .map(id => normalizeInternalId(id))
        .filter(id => id.length > 0);

    if (normalizedInternalIds.length === 0) {
        return list;
    }

    const allowedIds = new Set(normalizedInternalIds);
    return list.filter(row => allowedIds.has(normalizeInternalId(row.directionVariantId)));
}

export function mergeRowsAcrossDirections(
    rows: RidershipHistoryRecord[] | null | undefined,
): RidershipHistoryRecord[] {
    const list = rows ?? [];
    if (list.length === 0) {
        return [];
    }

    const mergedByStopId = new Map<string, RidershipHistoryRecord>();

    for (const row of list) {
        const stopId = String(row.stopId ?? '').trim();
        if (!stopId) {
            continue;
        }

        const existing = mergedByStopId.get(stopId);
        if (!existing) {
            mergedByStopId.set(stopId, {
                ...row,
                stopId,
                directionVariantId: undefined,
            });
            continue;
        }

        mergedByStopId.set(stopId, {
            ...existing,
            stopName: existing.stopName || row.stopName,
            stopCode: existing.stopCode || row.stopCode,
            stopExternalId: existing.stopExternalId || row.stopExternalId,
            routeId: existing.routeId || row.routeId,
            routeShortName: existing.routeShortName || row.routeShortName,
            directionVariantId: undefined,
            load: sumNumericField(existing.load, row.load),
            boarding: sumNumericField(existing.boarding, row.boarding),
            alighting: sumNumericField(existing.alighting, row.alighting),
            adjustedLoad: sumOptionalField(existing.adjustedLoad, row.adjustedLoad),
            adjustedBoarding: sumOptionalField(existing.adjustedBoarding, row.adjustedBoarding),
            adjustedAlighting: sumOptionalField(existing.adjustedAlighting, row.adjustedAlighting),
            loadSum: sumOptionalField(existing.loadSum, row.loadSum),
            boardingSum: sumOptionalField(existing.boardingSum, row.boardingSum),
            alightingSum: sumOptionalField(existing.alightingSum, row.alightingSum),
            adjustedLoadSum: sumOptionalField(existing.adjustedLoadSum, row.adjustedLoadSum),
            adjustedBoardingSum: sumOptionalField(existing.adjustedBoardingSum, row.adjustedBoardingSum),
            adjustedAlightingSum: sumOptionalField(existing.adjustedAlightingSum, row.adjustedAlightingSum),
            tripCount: sumNumericField(existing.tripCount, row.tripCount),
            dayCount: sumNumericField(existing.dayCount, row.dayCount),
        });
    }

    return Array.from(mergedByStopId.values());
}

export function resolveStopRankingRows(
    rows: RidershipHistoryRecord[] | null | undefined,
    selectedDirection: DirectionCatalogOption | null | undefined,
): RidershipHistoryRecord[] {
    const list = rows ?? [];
    const internalIds = selectedDirection?.internalIds ?? [];

    if (internalIds.length > 0) {
        return filterRowsByDirectionInternalIds(list, internalIds);
    }

    return mergeRowsAcrossDirections(list);
}
